<template>
  <div id="shopify-sync-page">
    <vx-card title="Shopify — Sincronizare catalog" class="mb-base">
      <div class="vx-row mb-6">
        <div class="vx-col w-full flex flex-wrap items-center justify-between">
          <div>
            <p v-if="notConfigured" class="text-warning">
              Nu ai configurat încă conexiunea Shopify.
              <router-link to="/settings/shopify-config">Configurează aici</router-link>
            </p>
            <p v-else>
              <vs-chip color="success">{{ syncedCount }} sincronizate</vs-chip>
              <vs-chip color="grey">{{ notSyncedCount }} nesincronizate</vs-chip>
              <vs-chip v-if="errorCount" color="danger">{{ errorCount }} erori</vs-chip>
            </p>
          </div>
          <div>
            <vs-button color="primary" icon-pack="feather" icon="icon-refresh-cw" @click="refresh" class="mr-2">
              Reîncarcă lista
            </vs-button>
            <vs-button color="warning" icon-pack="feather" icon="icon-plus" @click="openAdhoc">
              Produs ad-hoc
            </vs-button>
          </div>
        </div>
      </div>

      <div v-if="!notConfigured" class="vx-row mb-6">
        <div class="vx-col w-full flex flex-wrap items-center">
          <span class="mr-3 text-sm">
            <b>Top N implicit</b>
            <span class="text-grey"> — număr maxim de produse (cele mai populare) trimise per categorie</span>
          </span>
          <vs-input
            type="number"
            min="1"
            max="250"
            v-model.number="topNDefaultInput"
            class="mr-2"
            style="max-width: 110px;"
          />
          <vs-button
            size="small"
            color="primary"
            :disabled="savingTopNDefault || !isTopNDefaultValid || topNDefaultInput === currentTopNDefault"
            @click="saveTopNDefault"
          >
            {{ savingTopNDefault ? 'Se salvează…' : 'Salvează' }}
          </vs-button>
        </div>
      </div>

      <vs-table :data="categories" :max-items="50" pagination>
        <template slot="thead">
          <vs-th>#</vs-th>
          <vs-th sort-key="name_ro">Categoria (RO)</vs-th>
          <vs-th sort-key="eligible_product_count">Eligibile / Total</vs-th>
          <vs-th>Top N override</vs-th>
          <vs-th sort-key="synced_variants">Variante sincr.</vs-th>
          <vs-th sort-key="status">Status</vs-th>
          <vs-th sort-key="last_synced_at">Ultima sincronizare</vs-th>
          <vs-th>Acțiuni</vs-th>
        </template>

        <template slot-scope="{ data }">
          <vs-tr v-for="cat in data" :key="cat.id">
            <vs-td>{{ cat.id }}</vs-td>
            <vs-td>
              <b>{{ cat.name_ro || '—' }}</b>
              <div class="text-sm text-grey" v-if="cat.name_en">{{ cat.name_en }}</div>
            </vs-td>
            <vs-td>
              <b>{{ cat.eligible_product_count != null ? cat.eligible_product_count : cat.product_count }}</b>
              <span class="text-grey"> / {{ cat.product_count }}</span>
            </vs-td>
            <vs-td>
              <input
                type="number"
                min="1"
                max="250"
                :value="cat.sync_top_n_override"
                :placeholder="`(${cat.effective_top_n || currentTopNDefault})`"
                class="top-n-input"
                @change="onTopNOverrideChange(cat, $event)"
              />
            </vs-td>
            <vs-td>{{ cat.synced_variants }}</vs-td>
            <vs-td>
              <vs-chip v-if="cat.status === 'synced'" color="success">Sincronizat</vs-chip>
              <vs-chip v-else-if="cat.status === 'error'" color="danger">Eroare</vs-chip>
              <vs-chip v-else color="grey">Nesincronizat</vs-chip>
            </vs-td>
            <vs-td>
              <span v-if="cat.last_synced_at">{{ formatDate(cat.last_synced_at) }}</span>
              <span v-else>—</span>
            </vs-td>
            <vs-td>
              <vs-button
                size="small"
                color="primary"
                class="mr-2"
                :disabled="isBusy(cat.id) || cat.product_count === 0"
                @click="sync(cat)"
              >
                {{ isBusy(cat.id) ? '…' : (cat.status === 'synced' ? 'Re-sync' : 'Sync') }}
              </vs-button>
              <vs-button
                v-if="cat.status === 'synced'"
                size="small"
                color="danger"
                type="border"
                :disabled="isBusy(cat.id)"
                @click="confirmUnsync(cat)"
              >
                Șterge
              </vs-button>
            </vs-td>
          </vs-tr>
        </template>
      </vs-table>
    </vx-card>

    <vs-popup title="Produs ad-hoc Shopify (fără sincronizare XYLO)" :active.sync="adhocOpen">
      <p class="mb-4 text-sm text-grey">
        Acest produs va fi creat direct în Shopify. Nu va fi sincronizat cu catalogul XYLO —
        stocul, prețul și ștergerea se vor gestiona exclusiv din Shopify admin.
      </p>

      <vs-input label="Titlu (RO)" v-model="adhoc.title" class="w-full mb-3" />
      <vs-input label="SKU (opțional)" v-model="adhoc.sku" class="w-full mb-3" placeholder="ADHOC-..." />
      <div class="vx-row">
        <div class="vx-col md:w-1/2 w-full">
          <vs-input label="Preț RON" type="number" step="0.01" v-model="adhoc.price_ron" class="w-full mb-3" />
        </div>
        <div class="vx-col md:w-1/2 w-full">
          <vs-input label="Stoc inițial (bucăți)" type="number" v-model="adhoc.stock_pieces" class="w-full mb-3" />
        </div>
      </div>
      <vs-textarea label="Descriere (HTML permis)" v-model="adhoc.description_html" class="w-full mb-3" height="120px" />
      <vs-textarea
        label="URL-uri imagini (una pe linie)"
        v-model="adhoc.image_urls_text"
        class="w-full mb-2"
        height="80px"
        placeholder="https://..."
      />
      <div class="mb-3 flex items-center">
        <input
          ref="adhocImageInput"
          type="file"
          accept="image/*"
          class="hidden"
          @change="onAdhocImagePicked"
        />
        <vs-button
          size="small"
          color="primary"
          type="border"
          icon-pack="feather"
          icon="icon-upload"
          :disabled="adhocUploading"
          @click="$refs.adhocImageInput.click()"
        >
          {{ adhocUploading ? 'Se încarcă…' : 'Încarcă imagine' }}
        </vs-button>
        <span class="ml-3 text-sm text-grey">Imaginea se va adăuga automat în lista de URL-uri.</span>
      </div>

      <div class="flex justify-end">
        <vs-button color="grey" type="flat" @click="adhocOpen = false" class="mr-2">Anulează</vs-button>
        <vs-button color="primary" :disabled="adhocSaving || !adhoc.title" @click="submitAdhoc">
          {{ adhocSaving ? 'Se creează…' : 'Creează în Shopify' }}
        </vs-button>
      </div>
    </vs-popup>

    <sync-preview-modal
      :active.sync="previewOpen"
      :category-id="previewCatId"
      :category-name="previewCatName"
      @synced="onSyncDone"
    />

    <vs-popup title="Raport sincronizare" :active.sync="syncReportOpen">
      <div v-if="syncReport">
        <p class="mb-3">
          <b>{{ syncReport.category_name }}</b> —
          <vs-chip color="success">{{ syncReport.variants_synced }} variante sincronizate</vs-chip>
          <vs-chip v-if="syncReport.skipped_by_popularity" color="warning">
            {{ syncReport.skipped_by_popularity }} sărite (popularitate scăzută)
          </vs-chip>
          <vs-chip v-if="syncReport.skipped_by_duplicate.length" color="warning">
            {{ syncReport.skipped_by_duplicate.length }} sărite (dimensiuni duplicate)
          </vs-chip>
        </p>
        <p class="text-sm text-grey mb-3">
          Top N aplicat: <b>{{ syncReport.top_n_used }}</b> — total produse active în categorie: <b>{{ syncReport.total_active_products }}</b>
        </p>

        <div v-if="syncReport.skipped_by_duplicate.length" class="mb-4">
          <h5 class="mb-2">Produse sărite pentru dimensiuni duplicate</h5>
          <p class="text-sm text-grey mb-2">
            Aceste produse au aceleași dimensiuni variabile ca un alt produs păstrat (mai popular). Shopify nu acceptă variante cu aceleași opțiuni.
          </p>
          <ul class="text-sm">
            <li v-for="d in syncReport.skipped_by_duplicate" :key="d.product_id">
              <b>#{{ d.product_id }}</b> {{ d.product_name || '—' }}
              <span class="text-grey"> — duplicat al #{{ d.kept_product_id }} (semnătură: <code>{{ d.signature }}</code>)</span>
            </li>
          </ul>
        </div>

        <div v-if="syncReport.skipped_skus && syncReport.skipped_skus.length" class="mb-4">
          <h5 class="mb-2 text-warning">Avertisment: variante lipsă după productSet</h5>
          <p class="text-sm text-grey mb-2">
            Shopify a returnat mai puține variante decât am trimis. SKU-uri fără variantă:
          </p>
          <ul class="text-sm">
            <li v-for="sku in syncReport.skipped_skus" :key="sku">#{{ sku }}</li>
          </ul>
        </div>

        <div class="flex justify-end">
          <vs-button color="primary" @click="syncReportOpen = false">Închide</vs-button>
        </div>
      </div>
    </vs-popup>
  </div>
</template>

<script>
import moduleShopify from '@/store/shopify/moduleShopify.js'
import SyncPreviewModal from './SyncPreviewModal.vue'

export default {
  components: { SyncPreviewModal },
  data() {
    return {
      adhocOpen: false,
      adhocSaving: false,
      adhocUploading: false,
      adhoc: {
        title: '',
        sku: '',
        price_ron: '',
        stock_pieces: 0,
        description_html: '',
        image_urls_text: '',
      },
      topNDefaultInput: 20,
      savingTopNDefault: false,
      syncReportOpen: false,
      syncReport: null,
      previewOpen: false,
      previewCatId: null,
      previewCatName: '',
    }
  },
  computed: {
    categories() {
      return this.$store.state.shopify ? this.$store.state.shopify.categories : []
    },
    config() {
      return this.$store.state.shopify ? this.$store.state.shopify.config : null
    },
    notConfigured() {
      return !this.config || !this.config.shop_domain || !this.config.access_token
    },
    syncedCount() {
      return this.$store.getters['shopify/syncedCount'] || 0
    },
    notSyncedCount() {
      return this.$store.getters['shopify/notSyncedCount'] || 0
    },
    errorCount() {
      return this.$store.getters['shopify/errorCount'] || 0
    },
    currentTopNDefault() {
      return (this.config && parseInt(this.config.sync_top_n_default, 10)) || 20
    },
    isTopNDefaultValid() {
      const n = parseInt(this.topNDefaultInput, 10)
      return Number.isFinite(n) && n >= 1 && n <= 250
    },
  },
  watch: {
    config: {
      immediate: true,
      handler(val) {
        if (val && parseInt(val.sync_top_n_default, 10) > 0) {
          this.topNDefaultInput = parseInt(val.sync_top_n_default, 10)
        }
      },
    },
  },
  methods: {
    isBusy(id) {
      return this.$store.getters['shopify/isBusy'](id)
    },
    formatDate(d) {
      if (!d) return ''
      return new Date(d).toLocaleString('ro-RO')
    },
    refresh() {
      this.$store.dispatch('shopify/fetchCategories')
    },
    sync(cat) {
      this.previewCatId = cat.id
      this.previewCatName = cat.name_ro || cat.name_en || `#${cat.id}`
      this.previewOpen = true
    },
    onSyncDone(res) {
      const name = this.previewCatName
      const skipDup = (res.skipped_by_duplicate || []).length
      const skipPop = res.skipped_by_popularity || 0
      const skipMissing = (res.skipped_skus || []).length
      const hasSkips = skipDup || skipPop || skipMissing
      this.$vs.notify({
        color: hasSkips ? 'warning' : 'success',
        title: hasSkips ? 'Sincronizat cu avertismente' : 'Sincronizat',
        text: `"${name}" — ${res.variants_synced} variante, ${res.inventory_updated} stoc-uri actualizate.`,
        time: 10000,
      })
      if (hasSkips) {
        this.syncReport = Object.assign({}, res, { category_name: name })
        this.syncReportOpen = true
      }
    },
    saveTopNDefault() {
      if (!this.isTopNDefaultValid) return
      this.savingTopNDefault = true
      this.$store.dispatch('shopify/saveSyncDefaults', { sync_top_n_default: this.topNDefaultInput })
        .then(() => {
          this.$vs.notify({ color: 'success', title: 'Salvat', text: `Top N implicit = ${this.topNDefaultInput}` })
          this.$store.dispatch('shopify/fetchCategories')
        })
        .catch(err => {
          this.$vs.notify({
            color: 'danger',
            title: 'Eroare',
            text: (err.response && err.response.data && err.response.data.message) || err.message,
          })
        })
        .finally(() => { this.savingTopNDefault = false })
    },
    onTopNOverrideChange(cat, event) {
      const raw = event.target.value
      const top_n = raw === '' ? null : parseInt(raw, 10)
      if (top_n !== null && (!Number.isFinite(top_n) || top_n < 1 || top_n > 250)) {
        this.$vs.notify({
          color: 'danger',
          title: 'Valoare invalidă',
          text: 'Top N trebuie să fie între 1 și 250 (sau gol pentru a folosi valoarea implicită).',
        })
        event.target.value = cat.sync_top_n_override != null ? cat.sync_top_n_override : ''
        return
      }
      this.$store.dispatch('shopify/saveCategoryTopN', { id: cat.id, top_n })
        .catch(err => {
          this.$vs.notify({
            color: 'danger',
            title: 'Eroare',
            text: (err.response && err.response.data && err.response.data.message) || err.message,
          })
        })
    },
    confirmUnsync(cat) {
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: 'Confirmare ștergere',
        text: `Ștergi produsul Shopify pentru "${cat.name_ro}" ? Această acțiune nu poate fi anulată.`,
        accept: () => this.unsync(cat),
        acceptText: 'Șterge',
        cancelText: 'Anulează',
      })
    },
    unsync(cat) {
      this.$store.dispatch('shopify/unsyncCategory', cat.id)
        .then(() => {
          this.$vs.notify({
            color: 'success',
            title: 'Șters',
            text: `"${cat.name_ro}" a fost șters din Shopify.`,
          })
        })
        .catch(err => {
          this.$vs.notify({
            color: 'danger',
            title: 'Eroare',
            text: (err.response && err.response.data && err.response.data.message) || err.message,
          })
        })
    },
    onAdhocImagePicked(event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ''
      if (!file) return
      this.adhocUploading = true
      this.$store.dispatch('shopify/uploadAdhocImage', file)
        .then(res => {
          const existing = (this.adhoc.image_urls_text || '').trim()
          this.adhoc.image_urls_text = existing ? `${existing}\n${res.url}` : res.url
          this.$vs.notify({ color: 'success', title: 'Imagine încărcată', text: res.url })
        })
        .catch(err => {
          this.$vs.notify({
            color: 'danger',
            title: 'Eroare la încărcare',
            text: (err.response && err.response.data && err.response.data.message) || err.message,
          })
        })
        .finally(() => { this.adhocUploading = false })
    },
    openAdhoc() {
      this.adhoc = {
        title: '',
        sku: '',
        price_ron: '',
        stock_pieces: 0,
        description_html: '',
        image_urls_text: '',
      }
      this.adhocOpen = true
    },
    submitAdhoc() {
      const urls = (this.adhoc.image_urls_text || '')
        .split(/\r?\n/)
        .map(s => s.trim())
        .filter(Boolean)
      const payload = {
        title: this.adhoc.title,
        sku: this.adhoc.sku,
        price_ron: this.adhoc.price_ron,
        stock_pieces: this.adhoc.stock_pieces,
        description_html: this.adhoc.description_html,
        image_urls: urls,
      }
      this.adhocSaving = true
      this.$store.dispatch('shopify/createAdhocProduct', payload)
        .then(res => {
          this.$vs.notify({
            color: 'success',
            title: 'Produs creat',
            text: `SKU ${res.sku} — ${res.stock_pieces} bucăți, ${res.price_ron} RON.`,
            time: 10000,
          })
          this.adhocOpen = false
        })
        .catch(err => {
          this.$vs.notify({
            color: 'danger',
            title: 'Eroare',
            text: (err.response && err.response.data && err.response.data.message) || err.message,
            time: 15000,
          })
        })
        .finally(() => { this.adhocSaving = false })
    },
  },
  created() {
    if (!this.$store.state.shopify) {
      this.$store.registerModule('shopify', moduleShopify)
      moduleShopify.isRegistered = true
    }
    this.$store.dispatch('shopify/fetchConfig')
    this.$store.dispatch('shopify/fetchCategories')
  },
}
</script>

<style scoped>
.top-n-input {
  width: 72px;
  padding: 4px 6px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  text-align: center;
  font-size: 0.9rem;
}
.top-n-input:focus {
  outline: none;
  border-color: rgba(var(--vs-primary), 1);
}
</style>
